import { useState, useEffect, useCallback, useRef } from "react";
import useLearningPlatform from "../../../api/learningPlatform";
import {
  MessageSquare,
  Trash2,
  Trophy,
  Loader2,
  Medal,
  Pin,
  PinOff,
  Lock,
  Unlock,
  ArrowLeft,
  MessagesSquare,
  Tag,
  Plus,
  Image as ImageIcon,
  Video,
  Paperclip,
} from "lucide-react";
import {
  buttonClass,
  inputClass,
  SectionLoader,
  EmptyState,
  isNotFoundError,
} from "./shared";

const SUBTABS = [
  { id: "discussions", label: "Discussies", icon: MessageSquare },
  { id: "categories", label: "Categorieën", icon: Tag },
  { id: "leaderboard", label: "Leaderboard", icon: Trophy },
];

const useInlineConfirm = (timeoutMs = 3000) => {
  const [confirming, setConfirming] = useState(false);
  const timerRef = useRef(null);
  const start = () => {
    setConfirming(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setConfirming(false), timeoutMs);
  };
  const cancel = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setConfirming(false);
  };
  useEffect(() => () => timerRef.current && clearTimeout(timerRef.current), []);
  return [confirming, start, cancel];
};

const CommunityTab = ({ isDark }) => {
  const [subtab, setSubtab] = useState("discussions");

  return (
    <div className="space-y-4">
      <div
        className={`flex gap-1 p-1 rounded-lg w-fit ${
          isDark ? "bg-white/[0.04]" : "bg-black/[0.04]"
        }`}
      >
        {SUBTABS.map((s) => {
          const Icon = s.icon;
          const active = subtab === s.id;
          return (
            <button
              key={s.id}
              onClick={() => setSubtab(s.id)}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium transition-all ${
                active
                  ? isDark
                    ? "bg-white/[0.08] text-white"
                    : "bg-white text-gray-900 shadow-sm"
                  : isDark
                    ? "text-gray-500 hover:text-gray-200"
                    : "text-gray-500 hover:text-gray-800"
              }`}
            >
              <Icon size={12} />
              {s.label}
            </button>
          );
        })}
      </div>

      {subtab === "discussions" && <Discussions isDark={isDark} />}
      {subtab === "categories" && <Categories isDark={isDark} />}
      {subtab === "leaderboard" && <Leaderboard isDark={isDark} />}
    </div>
  );
};

// ----- Categories management -----

const Categories = ({ isDark }) => {
  const {
    getCategories,
    createCategory,
    updateCategory,
    deleteCategory,
  } = useLearningPlatform();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({ name: "", color: "#1E4FCF" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await getCategories();
      setCategories(Array.isArray(data) ? data : []);
    } catch (e) {
      if (!isNotFoundError(e)) setError("Kon categorieën niet laden");
    } finally {
      setLoading(false);
    }
  }, [getCategories]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async () => {
    if (!draft.name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await createCategory({ name: draft.name.trim(), color: draft.color });
      setDraft({ name: "", color: "#1E4FCF" });
      await load();
    } catch (e) {
      setError("Kon categorie niet aanmaken");
    } finally {
      setBusy(false);
    }
  };

  const handleRename = async (cat, newName) => {
    if (!newName.trim() || newName === cat.name) return;
    try {
      await updateCategory(cat.id, { name: newName.trim() });
      await load();
    } catch (e) {
      setError("Kon categorie niet wijzigen");
    }
  };

  const handleColor = async (cat, color) => {
    try {
      await updateCategory(cat.id, { color });
      await load();
    } catch (e) {
      setError("Kon kleur niet wijzigen");
    }
  };

  const handleDelete = async (cat) => {
    try {
      await deleteCategory(cat.id);
      await load();
    } catch (e) {
      setError("Kon categorie niet verwijderen");
    }
  };

  if (loading) return <SectionLoader isDark={isDark} />;

  return (
    <div className="space-y-3 max-w-xl">
      <div
        className={`flex items-end gap-2 rounded-md border p-3 ${
          isDark ? "border-white/[0.06] bg-white/[0.02]" : "border-gray-200 bg-gray-50"
        }`}
      >
        <input
          type="color"
          value={draft.color}
          onChange={(e) => setDraft((d) => ({ ...d, color: e.target.value }))}
          className={`h-9 w-10 rounded-md cursor-pointer border ${
            isDark ? "border-white/[0.08]" : "border-gray-200"
          }`}
        />
        <input
          type="text"
          value={draft.name}
          onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
          placeholder="Naam categorie (bv. Vragen)"
          className={inputClass(isDark)}
          onKeyDown={(e) => e.key === "Enter" && handleCreate()}
        />
        <button
          onClick={handleCreate}
          disabled={busy || !draft.name.trim()}
          className={buttonClass(isDark, "primary")}
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
          Voeg toe
        </button>
      </div>

      {error && (
        <div
          className={`text-[12px] px-3 py-2 rounded-md ${
            isDark ? "bg-red-500/10 text-red-400" : "bg-red-50 text-red-600"
          }`}
        >
          {error}
        </div>
      )}

      {categories.length === 0 ? (
        <EmptyState
          isDark={isDark}
          title="Nog geen categorieën"
          description="Groepeer discussies bv. in 'Vragen', 'Feedback', 'Aankondigingen'."
        />
      ) : (
        <div className="space-y-1.5">
          {categories.map((cat) => (
            <CategoryRow
              key={cat.id}
              category={cat}
              isDark={isDark}
              onRename={(name) => handleRename(cat, name)}
              onColor={(color) => handleColor(cat, color)}
              onDelete={() => handleDelete(cat)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const CategoryRow = ({ category, isDark, onRename, onColor, onDelete }) => {
  const [name, setName] = useState(category.name);
  const [confirming, askConfirm, cancelConfirm] = useInlineConfirm();
  useEffect(() => setName(category.name), [category.name]);

  return (
    <div
      className={`flex items-center gap-2 rounded-md border p-2 ${
        isDark ? "border-white/[0.06] bg-white/[0.02]" : "border-gray-200 bg-white"
      }`}
    >
      <input
        type="color"
        value={category.color || "#cccccc"}
        onChange={(e) => onColor(e.target.value)}
        className={`h-7 w-8 rounded cursor-pointer border ${
          isDark ? "border-white/[0.08]" : "border-gray-200"
        }`}
      />
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onBlur={() => onRename(name)}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.target.blur();
        }}
        className={inputClass(isDark)}
      />
      {confirming ? (
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => {
              cancelConfirm();
              onDelete();
            }}
            className={`text-[11px] px-2 py-0.5 rounded font-medium ${
              isDark ? "bg-red-500/20 text-red-300" : "bg-red-100 text-red-700"
            }`}
          >
            Verwijder
          </button>
          <button
            onClick={cancelConfirm}
            className={`text-[11px] px-2 py-0.5 rounded ${
              isDark ? "text-gray-400" : "text-gray-500"
            }`}
          >
            Annuleer
          </button>
        </div>
      ) : (
        <button
          onClick={askConfirm}
          className={`p-1.5 rounded ${
            isDark
              ? "text-gray-500 hover:text-red-400 hover:bg-red-500/10"
              : "text-gray-400 hover:text-red-500 hover:bg-red-50"
          }`}
        >
          <Trash2 size={12} />
        </button>
      )}
    </div>
  );
};

const Discussions = ({ isDark }) => {
  const {
    getDiscussions,
    getDiscussion,
    pinDiscussion,
    lockDiscussion,
    deleteDiscussion,
    deleteDiscussionPost,
  } = useLearningPlatform();

  const [discussions, setDiscussions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [activeDetail, setActiveDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionBusy, setActionBusy] = useState({});
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await getDiscussions();
      setDiscussions(Array.isArray(data) ? data : []);
    } catch (e) {
      if (isNotFoundError(e)) {
        setDiscussions([]);
      } else {
        setError("Kon discussies niet laden");
      }
    } finally {
      setLoading(false);
    }
  }, [getDiscussions]);

  useEffect(() => {
    load();
  }, [load]);

  const openDetail = useCallback(
    async (id) => {
      setActiveId(id);
      setActiveDetail(null);
      setDetailLoading(true);
      try {
        const data = await getDiscussion(id);
        setActiveDetail(data);
      } catch (e) {
        setError("Kon discussie niet laden");
        setActiveId(null);
      } finally {
        setDetailLoading(false);
      }
    },
    [getDiscussion]
  );

  const closeDetail = () => {
    setActiveId(null);
    setActiveDetail(null);
  };

  const setBusy = (key, value) =>
    setActionBusy((prev) => ({ ...prev, [key]: value }));

  const handleTogglePin = async (discussion) => {
    const key = `pin-${discussion.id}`;
    setBusy(key, true);
    try {
      const updated = await pinDiscussion(discussion.id, !discussion.is_pinned);
      setDiscussions((prev) =>
        prev.map((d) => (d.id === discussion.id ? { ...d, ...updated } : d))
      );
      if (activeId === discussion.id && activeDetail) {
        setActiveDetail({ ...activeDetail, ...updated });
      }
    } catch (e) {
      setError("Kon pin-status niet wijzigen");
    } finally {
      setBusy(key, false);
    }
  };

  const handleToggleLock = async (discussion) => {
    const key = `lock-${discussion.id}`;
    setBusy(key, true);
    try {
      const updated = await lockDiscussion(discussion.id, !discussion.is_locked);
      setDiscussions((prev) =>
        prev.map((d) => (d.id === discussion.id ? { ...d, ...updated } : d))
      );
      if (activeId === discussion.id && activeDetail) {
        setActiveDetail({ ...activeDetail, ...updated });
      }
    } catch (e) {
      setError("Kon lock-status niet wijzigen");
    } finally {
      setBusy(key, false);
    }
  };

  const handleDeleteDiscussion = async (discussion) => {
    const key = `del-${discussion.id}`;
    setBusy(key, true);
    try {
      await deleteDiscussion(discussion.id);
      setDiscussions((prev) => prev.filter((d) => d.id !== discussion.id));
      if (activeId === discussion.id) {
        closeDetail();
      }
    } catch (e) {
      setError("Kon discussie niet verwijderen");
    } finally {
      setBusy(key, false);
    }
  };

  const handleDeletePost = async (post) => {
    const key = `delpost-${post.id}`;
    setBusy(key, true);
    try {
      await deleteDiscussionPost(post.id);
      setActiveDetail((prev) =>
        prev
          ? {
              ...prev,
              posts: (prev.posts || []).filter((p) => p.id !== post.id),
              reply_count: Math.max(0, Number(prev.reply_count || 0) - 1),
            }
          : prev
      );
      setDiscussions((prev) =>
        prev.map((d) =>
          d.id === post.discussion_id
            ? {
                ...d,
                reply_count: Math.max(0, Number(d.reply_count || 0) - 1),
              }
            : d
        )
      );
    } catch (e) {
      setError("Kon post niet verwijderen");
    } finally {
      setBusy(key, false);
    }
  };

  if (loading) return <SectionLoader isDark={isDark} />;

  return (
    <div className="space-y-3">
      {error && (
        <div
          className={`text-[12px] px-3 py-2 rounded-md flex items-center justify-between ${
            isDark ? "bg-red-500/10 text-red-400" : "bg-red-50 text-red-600"
          }`}
        >
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            className="opacity-60 hover:opacity-100 text-[16px] leading-none"
          >
            ×
          </button>
        </div>
      )}

      {activeId ? (
        <DiscussionDetail
          isDark={isDark}
          loading={detailLoading}
          discussion={activeDetail}
          actionBusy={actionBusy}
          onBack={closeDetail}
          onTogglePin={handleTogglePin}
          onToggleLock={handleToggleLock}
          onDeleteDiscussion={handleDeleteDiscussion}
          onDeletePost={handleDeletePost}
        />
      ) : discussions.length === 0 ? (
        <EmptyState
          isDark={isDark}
          title="Nog geen discussies"
          description="Studenten kunnen zelf discussies starten op het leerplatform. Zodra ze actief zijn verschijnen ze hier voor moderatie."
        />
      ) : (
        <div className="space-y-2">
          {discussions.map((d) => (
            <DiscussionRow
              key={d.id}
              isDark={isDark}
              discussion={d}
              actionBusy={actionBusy}
              onOpen={() => openDetail(d.id)}
              onTogglePin={() => handleTogglePin(d)}
              onToggleLock={() => handleToggleLock(d)}
              onDelete={() => handleDeleteDiscussion(d)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const DiscussionRow = ({
  isDark,
  discussion,
  actionBusy,
  onOpen,
  onTogglePin,
  onToggleLock,
  onDelete,
}) => {
  const [confirming, askConfirm, cancelConfirm] = useInlineConfirm();
  const pinBusy = actionBusy[`pin-${discussion.id}`];
  const lockBusy = actionBusy[`lock-${discussion.id}`];
  const delBusy = actionBusy[`del-${discussion.id}`];

  const initial = (discussion.author_name || "?").charAt(0).toUpperCase();

  return (
    <div
      className={`rounded-lg border transition-colors ${
        isDark
          ? "bg-white/[0.02] border-white/[0.06] hover:bg-white/[0.04]"
          : "bg-white border-gray-200 hover:border-gray-300"
      }`}
    >
      <div className="flex items-center gap-3 px-3 py-2.5">
        <button
          type="button"
          onClick={onOpen}
          className="flex items-center gap-3 flex-1 min-w-0 text-left"
        >
          {discussion.author_avatar_url ? (
            <img
              src={discussion.author_avatar_url}
              alt=""
              className="h-8 w-8 rounded-full object-cover flex-shrink-0"
            />
          ) : (
            <div
              className={`h-8 w-8 rounded-full flex items-center justify-center text-[11px] font-semibold flex-shrink-0 ${
                isDark
                  ? "bg-white/[0.08] text-gray-300"
                  : "bg-gray-100 text-gray-600"
              }`}
            >
              {initial}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              {discussion.is_pinned ? (
                <Pin
                  size={11}
                  className={isDark ? "text-amber-400" : "text-amber-500"}
                />
              ) : null}
              {discussion.is_locked ? (
                <Lock
                  size={11}
                  className={isDark ? "text-gray-500" : "text-gray-400"}
                />
              ) : null}
              {discussion.category_name ? (
                <span
                  className="text-[10px] px-1.5 py-0.5 rounded font-medium"
                  style={{
                    background: (discussion.category_color || "#888") + "22",
                    color: discussion.category_color || (isDark ? "#9ca3af" : "#374151"),
                  }}
                >
                  {discussion.category_name}
                </span>
              ) : null}
              <span
                className={`text-[13px] font-medium truncate ${
                  isDark ? "text-gray-200" : "text-gray-900"
                }`}
              >
                {discussion.title || "(geen titel)"}
              </span>
            </div>
            <div
              className={`text-[11px] flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5 ${
                isDark ? "text-gray-500" : "text-gray-500"
              }`}
            >
              <span>{discussion.author_name || "Onbekend"}</span>
              <span>{formatRelative(discussion.created_at)}</span>
              <span className="inline-flex items-center gap-1">
                <MessagesSquare size={10} />
                {Number(discussion.reply_count || 0)}{" "}
                {Number(discussion.reply_count || 0) === 1
                  ? "reactie"
                  : "reacties"}
              </span>
              {Array.isArray(discussion.attachments) && discussion.attachments.length > 0 && (
                <span className="inline-flex items-center gap-1">
                  <Paperclip size={10} /> {discussion.attachments.length}
                </span>
              )}
            </div>
          </div>
        </button>

        {confirming ? (
          <div
            className={`flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-md ${
              isDark ? "bg-red-500/10 text-red-400" : "bg-red-50 text-red-600"
            }`}
          >
            <span className="font-medium">Zeker?</span>
            <button
              onClick={() => {
                cancelConfirm();
                onDelete();
              }}
              disabled={delBusy}
              className={`px-2 py-0.5 rounded font-medium ${
                isDark
                  ? "bg-red-500/20 hover:bg-red-500/30"
                  : "bg-red-100 hover:bg-red-200"
              }`}
            >
              {delBusy ? <Loader2 size={11} className="animate-spin" /> : "Ja"}
            </button>
            <button
              onClick={cancelConfirm}
              className={`px-2 py-0.5 rounded ${
                isDark
                  ? "text-gray-400 hover:text-gray-200"
                  : "text-gray-500 hover:text-gray-800"
              }`}
            >
              Nee
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-1 flex-shrink-0">
            <IconButton
              isDark={isDark}
              onClick={onTogglePin}
              busy={pinBusy}
              title={discussion.is_pinned ? "Unpin" : "Pin bovenaan"}
              active={!!discussion.is_pinned}
            >
              {discussion.is_pinned ? <PinOff size={12} /> : <Pin size={12} />}
            </IconButton>
            <IconButton
              isDark={isDark}
              onClick={onToggleLock}
              busy={lockBusy}
              title={
                discussion.is_locked
                  ? "Discussie heropenen"
                  : "Discussie sluiten"
              }
              active={!!discussion.is_locked}
            >
              {discussion.is_locked ? (
                <Unlock size={12} />
              ) : (
                <Lock size={12} />
              )}
            </IconButton>
            <button
              type="button"
              onClick={askConfirm}
              className={`p-1.5 rounded transition-colors ${
                isDark
                  ? "text-gray-500 hover:text-red-400 hover:bg-red-500/10"
                  : "text-gray-400 hover:text-red-500 hover:bg-red-50"
              }`}
              title="Verwijder discussie"
            >
              <Trash2 size={12} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const IconButton = ({ isDark, onClick, busy, title, active, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={busy}
    title={title}
    className={`p-1.5 rounded transition-colors ${
      active
        ? isDark
          ? "text-amber-300 bg-amber-500/10 hover:bg-amber-500/20"
          : "text-amber-600 bg-amber-50 hover:bg-amber-100"
        : isDark
          ? "text-gray-500 hover:text-gray-200 hover:bg-white/[0.06]"
          : "text-gray-400 hover:text-gray-700 hover:bg-gray-100"
    }`}
  >
    {busy ? <Loader2 size={12} className="animate-spin" /> : children}
  </button>
);

const DiscussionDetail = ({
  isDark,
  loading,
  discussion,
  actionBusy,
  onBack,
  onTogglePin,
  onToggleLock,
  onDeleteDiscussion,
  onDeletePost,
}) => {
  const [confirming, askConfirm, cancelConfirm] = useInlineConfirm();

  if (loading || !discussion) {
    return (
      <div
        className={`rounded-lg border ${
          isDark
            ? "bg-white/[0.02] border-white/[0.06]"
            : "bg-white border-gray-200"
        }`}
      >
        <div className="px-3 py-2.5 border-b flex items-center gap-2"
          style={{ borderColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)" }}>
          <button
            onClick={onBack}
            className={buttonClass(isDark, "ghost")}
          >
            <ArrowLeft size={12} /> Terug
          </button>
        </div>
        <SectionLoader isDark={isDark} />
      </div>
    );
  }

  const pinBusy = actionBusy[`pin-${discussion.id}`];
  const lockBusy = actionBusy[`lock-${discussion.id}`];
  const delBusy = actionBusy[`del-${discussion.id}`];
  const posts = discussion.posts || [];

  return (
    <div
      className={`rounded-lg border overflow-hidden ${
        isDark
          ? "bg-white/[0.02] border-white/[0.06]"
          : "bg-white border-gray-200"
      }`}
    >
      <div
        className={`flex items-center gap-2 px-3 py-2.5 border-b ${
          isDark ? "border-white/[0.06]" : "border-gray-200"
        }`}
      >
        <button
          onClick={onBack}
          className={buttonClass(isDark, "ghost")}
        >
          <ArrowLeft size={12} /> Terug
        </button>
        <div className="flex-1" />
        <IconButton
          isDark={isDark}
          onClick={() => onTogglePin(discussion)}
          busy={pinBusy}
          title={discussion.is_pinned ? "Unpin" : "Pin bovenaan"}
          active={!!discussion.is_pinned}
        >
          {discussion.is_pinned ? <PinOff size={12} /> : <Pin size={12} />}
        </IconButton>
        <IconButton
          isDark={isDark}
          onClick={() => onToggleLock(discussion)}
          busy={lockBusy}
          title={discussion.is_locked ? "Heropenen" : "Sluiten"}
          active={!!discussion.is_locked}
        >
          {discussion.is_locked ? <Unlock size={12} /> : <Lock size={12} />}
        </IconButton>
        {confirming ? (
          <div
            className={`flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-md ${
              isDark ? "bg-red-500/10 text-red-400" : "bg-red-50 text-red-600"
            }`}
          >
            <span className="font-medium">Verwijderen?</span>
            <button
              onClick={() => {
                cancelConfirm();
                onDeleteDiscussion(discussion);
              }}
              disabled={delBusy}
              className={`px-2 py-0.5 rounded font-medium ${
                isDark
                  ? "bg-red-500/20 hover:bg-red-500/30"
                  : "bg-red-100 hover:bg-red-200"
              }`}
            >
              {delBusy ? <Loader2 size={11} className="animate-spin" /> : "Ja"}
            </button>
            <button
              onClick={cancelConfirm}
              className={`px-2 py-0.5 rounded ${
                isDark
                  ? "text-gray-400 hover:text-gray-200"
                  : "text-gray-500 hover:text-gray-800"
              }`}
            >
              Nee
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={askConfirm}
            className={`p-1.5 rounded transition-colors ${
              isDark
                ? "text-gray-500 hover:text-red-400 hover:bg-red-500/10"
                : "text-gray-400 hover:text-red-500 hover:bg-red-50"
            }`}
            title="Verwijder discussie"
          >
            <Trash2 size={12} />
          </button>
        )}
      </div>

      <div className="px-4 py-4">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          {discussion.is_pinned ? (
            <span
              className={`text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${
                isDark
                  ? "bg-amber-500/10 text-amber-300"
                  : "bg-amber-50 text-amber-600"
              }`}
            >
              <Pin size={9} /> Gepind
            </span>
          ) : null}
          {discussion.is_locked ? (
            <span
              className={`text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${
                isDark
                  ? "bg-white/[0.08] text-gray-300"
                  : "bg-gray-100 text-gray-600"
              }`}
            >
              <Lock size={9} /> Gesloten
            </span>
          ) : null}
        </div>
        <h2
          className={`text-[16px] font-semibold ${
            isDark ? "text-white" : "text-gray-900"
          }`}
        >
          {discussion.title || "(geen titel)"}
        </h2>
        <div
          className={`text-[11.5px] mt-1 flex flex-wrap gap-x-3 ${
            isDark ? "text-gray-500" : "text-gray-500"
          }`}
        >
          <span>{discussion.author_name || "Onbekend"}</span>
          <span>{formatRelative(discussion.created_at)}</span>
          <span>
            {posts.length} {posts.length === 1 ? "reactie" : "reacties"}
          </span>
        </div>
        {discussion.body && (
          <p
            className={`text-[13px] mt-3 leading-relaxed whitespace-pre-wrap ${
              isDark ? "text-gray-300" : "text-gray-700"
            }`}
          >
            {discussion.body}
          </p>
        )}
        <AttachmentList
          attachments={discussion.attachments}
          isDark={isDark}
        />
        <ReactionsRow
          reactions={discussion.reactions}
          isDark={isDark}
        />
      </div>

      <div
        className={`border-t ${
          isDark ? "border-white/[0.06]" : "border-gray-200"
        }`}
      >
        <div
          className={`px-4 py-2 text-[11px] uppercase tracking-wider font-semibold ${
            isDark
              ? "text-gray-500 bg-white/[0.02]"
              : "text-gray-500 bg-gray-50"
          }`}
        >
          Reacties
        </div>
        {posts.length === 0 ? (
          <div
            className={`px-4 py-6 text-center text-[12px] ${
              isDark ? "text-gray-500" : "text-gray-500"
            }`}
          >
            Nog geen reacties op deze discussie.
          </div>
        ) : (
          <div>
            {posts.map((post) => (
              <PostRow
                key={post.id}
                isDark={isDark}
                post={post}
                busy={actionBusy[`delpost-${post.id}`]}
                onDelete={() => onDeletePost(post)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const PostRow = ({ isDark, post, busy, onDelete }) => {
  const [confirming, askConfirm, cancelConfirm] = useInlineConfirm();
  const initial = (post.author_name || "?").charAt(0).toUpperCase();

  return (
    <div
      className={`flex items-start gap-3 px-4 py-3 border-t first:border-t-0 ${
        isDark ? "border-white/[0.04]" : "border-gray-100"
      }`}
    >
      {post.author_avatar_url ? (
        <img
          src={post.author_avatar_url}
          alt=""
          className="h-7 w-7 rounded-full object-cover flex-shrink-0"
        />
      ) : (
        <div
          className={`h-7 w-7 rounded-full flex items-center justify-center text-[10px] font-semibold flex-shrink-0 ${
            isDark
              ? "bg-white/[0.08] text-gray-300"
              : "bg-gray-100 text-gray-600"
          }`}
        >
          {initial}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span
            className={`text-[12px] font-medium ${
              isDark ? "text-gray-200" : "text-gray-900"
            }`}
          >
            {post.author_name || "Onbekend"}
          </span>
          <span
            className={`text-[11px] ${
              isDark ? "text-gray-600" : "text-gray-400"
            }`}
          >
            {formatRelative(post.created_at)}
          </span>
        </div>
        <p
          className={`text-[12.5px] mt-1 leading-relaxed whitespace-pre-wrap ${
            isDark ? "text-gray-300" : "text-gray-700"
          }`}
        >
          {post.body}
        </p>
        <AttachmentList attachments={post.attachments} isDark={isDark} />
        <ReactionsRow reactions={post.reactions} isDark={isDark} />
      </div>
      {confirming ? (
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <span
            className={`text-[10px] font-medium ${
              isDark ? "text-red-400" : "text-red-600"
            }`}
          >
            Verwijderen?
          </span>
          <button
            onClick={() => {
              cancelConfirm();
              onDelete();
            }}
            disabled={busy}
            className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
              isDark
                ? "bg-red-500/20 text-red-300 hover:bg-red-500/30"
                : "bg-red-100 text-red-700 hover:bg-red-200"
            }`}
          >
            {busy ? <Loader2 size={10} className="animate-spin" /> : "Ja"}
          </button>
          <button
            onClick={cancelConfirm}
            className={`text-[10px] px-1.5 py-0.5 rounded ${
              isDark
                ? "text-gray-400 hover:text-gray-200"
                : "text-gray-500 hover:text-gray-800"
            }`}
          >
            Nee
          </button>
        </div>
      ) : (
        <button
          onClick={askConfirm}
          className={`p-1.5 rounded transition-colors flex-shrink-0 ${
            isDark
              ? "text-gray-500 hover:text-red-400 hover:bg-red-500/10"
              : "text-gray-400 hover:text-red-500 hover:bg-red-50"
          }`}
          title="Verwijder reactie"
        >
          <Trash2 size={12} />
        </button>
      )}
    </div>
  );
};

const Leaderboard = ({ isDark }) => {
  const { getLeaderboard } = useLearningPlatform();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getLeaderboard();
        if (!cancelled) setEntries(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled && isNotFoundError(e)) setEntries([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getLeaderboard]);

  if (loading) return <SectionLoader isDark={isDark} />;

  if (entries.length === 0) {
    return (
      <EmptyState
        isDark={isDark}
        title="Leaderboard is leeg"
        description="Studenten verzamelen punten door cursussen af te ronden, posts te plaatsen en evenementen bij te wonen."
      />
    );
  }

  return (
    <div
      className={`rounded-lg border overflow-hidden ${
        isDark ? "border-white/[0.06]" : "border-gray-200"
      }`}
    >
      <table className="w-full text-[12px]">
        <thead
          className={`text-left ${
            isDark
              ? "bg-white/[0.02] text-gray-500"
              : "bg-gray-50 text-gray-500"
          }`}
        >
          <tr>
            <th className="px-3 py-2 w-12 font-medium text-[11px] uppercase tracking-wider">
              #
            </th>
            <th className="px-3 py-2 font-medium text-[11px] uppercase tracking-wider">
              Student
            </th>
            <th className="px-3 py-2 text-right font-medium text-[11px] uppercase tracking-wider">
              Punten
            </th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry, idx) => (
            <tr
              key={entry.id}
              className={`border-t ${
                isDark ? "border-white/[0.04]" : "border-gray-100"
              }`}
            >
              <td className="px-3 py-2.5">
                <RankBadge rank={idx + 1} isDark={isDark} />
              </td>
              <td className="px-3 py-2.5">
                <div className="flex items-center gap-2">
                  {entry.avatarUrl ? (
                    <img
                      src={entry.avatarUrl}
                      alt=""
                      className="h-6 w-6 rounded-full object-cover"
                    />
                  ) : (
                    <div
                      className={`h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-semibold ${
                        isDark
                          ? "bg-white/[0.08] text-gray-300"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {entry.name?.charAt(0)?.toUpperCase() || "?"}
                    </div>
                  )}
                  <span className={isDark ? "text-gray-200" : "text-gray-900"}>
                    {entry.name || entry.email || "Onbekend"}
                  </span>
                </div>
              </td>
              <td
                className={`px-3 py-2.5 text-right font-mono ${
                  isDark ? "text-gray-200" : "text-gray-800"
                }`}
              >
                {entry.points ?? 0}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const RankBadge = ({ rank, isDark }) => {
  if (rank === 1)
    return (
      <span className="inline-flex items-center gap-1 text-amber-500 font-semibold">
        <Medal size={13} /> 1
      </span>
    );
  if (rank === 2)
    return (
      <span className="inline-flex items-center gap-1 text-zinc-400 font-semibold">
        <Medal size={13} /> 2
      </span>
    );
  if (rank === 3)
    return (
      <span className="inline-flex items-center gap-1 text-orange-600 font-semibold">
        <Medal size={13} /> 3
      </span>
    );
  return (
    <span
      className={`font-mono ${isDark ? "text-gray-500" : "text-gray-400"}`}
    >
      {rank}
    </span>
  );
};

const AttachmentList = ({ attachments, isDark }) => {
  if (!Array.isArray(attachments) || attachments.length === 0) return null;
  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {attachments.map((att, i) => {
        if (att.kind === "image" && att.url) {
          return (
            <a
              key={i}
              href={att.url}
              target="_blank"
              rel="noreferrer"
              className="block"
            >
              <img
                src={att.url}
                alt={att.name || ""}
                className="h-28 w-40 object-cover rounded border"
                style={{
                  borderColor: isDark ? "rgba(255,255,255,0.08)" : "#e5e7eb",
                }}
              />
            </a>
          );
        }
        if (att.kind === "video" && att.url) {
          return (
            <video
              key={i}
              src={att.url}
              controls
              className="h-28 rounded border"
              style={{
                borderColor: isDark ? "rgba(255,255,255,0.08)" : "#e5e7eb",
              }}
            />
          );
        }
        // file or embed fallback
        const Icon =
          att.kind === "video" ? Video : att.kind === "image" ? ImageIcon : Paperclip;
        return (
          <a
            key={i}
            href={att.url || "#"}
            target="_blank"
            rel="noreferrer"
            className={`inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded border ${
              isDark
                ? "border-white/[0.08] text-gray-300 hover:bg-white/[0.04]"
                : "border-gray-200 text-gray-700 hover:bg-gray-50"
            }`}
          >
            <Icon size={11} /> {att.name || att.kind}
          </a>
        );
      })}
    </div>
  );
};

const ReactionsRow = ({ reactions, isDark }) => {
  if (!Array.isArray(reactions) || reactions.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1">
      {reactions.map((r, i) => (
        <span
          key={i}
          className={`inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded border ${
            r.me
              ? isDark
                ? "bg-amber-500/10 border-amber-500/30 text-amber-300"
                : "bg-amber-50 border-amber-200 text-amber-700"
              : isDark
                ? "bg-white/[0.04] border-white/[0.06] text-gray-300"
                : "bg-gray-50 border-gray-200 text-gray-700"
          }`}
        >
          <span className="text-[13px] leading-none">{r.emoji}</span>
          <span className="font-mono">{r.count}</span>
        </span>
      ))}
    </div>
  );
};

const formatRelative = (iso) => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const diff = Date.now() - date.getTime();
  const min = 1000 * 60;
  const hour = min * 60;
  const day = hour * 24;
  if (diff < min) return "net";
  if (diff < hour) return `${Math.floor(diff / min)}m`;
  if (diff < day) return `${Math.floor(diff / hour)}u`;
  return `${Math.floor(diff / day)}d`;
};

export default CommunityTab;
